import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { PencomCard } from "@/components/cards/PencomCard";
import { DataTable } from "@/components/common/DataTable";
import type { TableColumn } from "@/types";
import { formatNGN } from "@/utils/moneyFormatter";
import { useGetContributionByMonth } from "@/hooks/compliance/compliance.hook";
import type { EmployeeContribution } from "@/models/responses/compliance/employer-contributions.response";
import { PageSkeleton } from "@/components/common/SkeletonLoader";
import { PaginationComponent } from "@/components/dashboard/PaginationComponent";
import { PencomRoutes } from "@/router/PencomRoutes";
import { EContributionType } from "@/constants/enums/contribution-type.enum";
import { useAuthStore } from "@/stores/authStore";
import {
  CONTRIBUTION_TYPE_LABEL,
  yearsFromIncorporation,
} from "./contributionHistory.utils";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const PAGE_SIZE_OPTIONS = [50, 100, 200] as const;

const ContributionsByMonthPage = () => {
  const { year = "", month = "" } = useParams<{
    year: string;
    month: string;
  }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentEmployer = useAuthStore((s) => s.currentEmployer);
  const currentCalendarYear = new Date().getFullYear();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState<(typeof PAGE_SIZE_OPTIONS)[number]>(50);

  // Optional COM/PEN filter carried over from the contribution-history row.
  // When present it both filters the data and labels the page; when absent the
  // page shows every contribution type for the month.
  const contributionTypeParam = searchParams.get("contributionType");
  const contributionType =
    contributionTypeParam === EContributionType.PENALTY ||
    contributionTypeParam === EContributionType.CONTRIBUTION
      ? contributionTypeParam
      : undefined;

  const { data, isLoading } = useGetContributionByMonth(
    year,
    month,
    page,
    limit,
    contributionType,
  );

  const title = useMemo(() => {
    const monthNum = parseInt(month, 10);
    const monthName =
      monthNum >= 1 && monthNum <= 12 ? MONTH_NAMES[monthNum - 1] : month;
    const typeLabel = contributionType
      ? CONTRIBUTION_TYPE_LABEL[contributionType]
      : undefined;
    return typeLabel
      ? `${typeLabel} - ${monthName} ${year}`
      : `${monthName} ${year}`;
  }, [year, month, contributionType]);

  const yearOptions = useMemo(() => {
    const incorporationDate = currentEmployer?.incorporationDate;
    const base =
      typeof incorporationDate === "string" && incorporationDate
        ? yearsFromIncorporation(incorporationDate, currentCalendarYear)
        : [currentCalendarYear];
    const yNum = parseInt(year, 10);
    if (Number.isFinite(yNum) && !base.includes(yNum)) {
      return [...base, yNum].sort((a, b) => b - a);
    }
    return base;
  }, [currentEmployer?.incorporationDate, currentCalendarYear, year]);

  const columns: TableColumn<EmployeeContribution>[] = useMemo(
    () => [
      {
        key: "employee",
        header: "Employee",
        render: (value) => (
          <span className="text-medium-dark-grey font-regular">
            {String(value ?? "")}
          </span>
        ),
      },
      {
        key: "rsaPin",
        header: "RSA PIN",
        render: (value) => (
          <span className="text-medium-dark-grey font-regular">
            {String(value ?? "")}
          </span>
        ),
      },
      {
        key: "contributionType",
        header: "Contribution Type",
        render: (_, record) => {
          const type = record.contributionType ?? contributionType;
          return (
            <span className="text-medium-dark-grey font-regular">
              {type ? CONTRIBUTION_TYPE_LABEL[type] ?? type : "—"}
            </span>
          );
        },
      },
      {
        key: "employeeContribution",
        header: "Employee Contribution",
        render: (value) => (
          <span className="text-black-dark-grey font-regular">
            {formatNGN(Number(value ?? 0))}
          </span>
        ),
      },
      {
        key: "pfcId",
        header: "Employer Contribution",
        render: (_, record) => {
          const emp = Number(record.employeeContribution ?? 0);
          const total = Number(record.totalContribution ?? 0);
          const employer = total - emp;
          return (
            <span className="text-black-dark-grey font-regular">
              {formatNGN(employer)}
            </span>
          );
        },
      },
      {
        key: "totalContribution",
        header: "Total Contribution",
        render: (value) => (
          <span className="text-black-dark-grey font-semibold">
            {formatNGN(Number(value ?? 0))}
          </span>
        ),
      },
    ],
    [contributionType]
  );

  const tableData = data?.data ?? [];
  const pagination = data?.pagination;
  const total = pagination?.total ?? 0;
  const totalPages = Math.max(1, pagination?.totalPages ?? 1);
  const start = total === 0 ? 0 : (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  const handleBack = () => {
    navigate(PencomRoutes.CONTRIBUTION_HISTORY);
  };

  const handleYearChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newYear = event.target.value;
    const path = PencomRoutes.CONTRIBUTION_BY_MONTH.replace(
      ":year",
      newYear
    ).replace(":month", month);
    const qs = searchParams.toString();
    navigate(qs ? `${path}?${qs}` : path);
  };

  const handleLimitChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setLimit(Number(event.target.value) as (typeof PAGE_SIZE_OPTIONS)[number]);
    setPage(1);
  };

  useEffect(() => {
    if (!pagination) return;
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages, pagination]);

  useEffect(() => {
    setPage(1);
  }, [year, month]);

  if (isLoading) {
    return <PageSkeleton tableColumns={6} tableRows={8} />;
  }

  return (
    <div className="w-full flex flex-col min-h-screen">
      <div className="w-full flex flex-col max-w-[1240px] mx-auto mt-6 px-4">
        <PencomCard
          title={title}
          extraClassName="justify-between flex-wrap gap-y-2"
          extra={
            <div className="flex flex-row flex-wrap items-center gap-x-3 gap-y-2">
              <div className="flex items-center gap-x-2">
                <label
                  htmlFor="contribution-by-month-year"
                  className="font-clash-display text-sm font-medium text-medium-dark-grey whitespace-nowrap"
                >
                  Year
                </label>
                <select
                  id="contribution-by-month-year"
                  value={year}
                  onChange={handleYearChange}
                  className="cursor-pointer rounded-lg border border-stroke bg-white px-3 py-2 font-clash-display text-sm font-medium text-medium-dark-grey focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary-button min-w-[88px]"
                >
                  {yearOptions.map((y) => (
                    <option key={y} value={String(y)}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                onClick={handleBack}
                className="px-4 py-2 rounded-lg border border-stroke bg-white text-medium-dark-grey font-clash-display font-medium text-sm hover:bg-off-white transition-colors cursor-pointer"
              >
                Back
              </button>
            </div>
          }
        >
          <div className="mt-6">
            <DataTable
              data={tableData}
              columns={columns}
              currentPage={page}
              totalPages={totalPages}
              onPageChange={() => {}}
              showPagination={false}
              emptyMessage="No contributions for this month"
            />

            {total > 0 && (
              <div className="flex flex-row items-center justify-between mt-6 flex-wrap gap-4">
                <div className="flex items-center gap-x-4 flex-wrap">
                  <p className="font-clash-display text-medium-dark-grey text-sm font-regular">
                    Showing {start} to {end} of {total} results
                  </p>
                  <div className="flex items-center gap-x-2">
                    <label
                      htmlFor="page-size"
                      className="font-clash-display text-medium-dark-grey text-sm font-regular"
                    >
                      Rows per page
                    </label>
                    <select
                      id="page-size"
                      value={limit}
                      onChange={handleLimitChange}
                      className="cursor-pointer rounded-md border border-stroke bg-white px-2 py-1 font-clash-display text-sm text-medium-dark-grey focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary-button"
                    >
                      {PAGE_SIZE_OPTIONS.map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <PaginationComponent
                  currentPage={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                />
              </div>
            )}
          </div>
        </PencomCard>
      </div>
    </div>
  );
};

export default ContributionsByMonthPage;
